import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import "./featured.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const Featured = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${REACT_APP_API_URL}/products`);
        const data = await res.json();
        
        // Get featured products from different categories
        const featuredCategories = ["POP Phone", "iPhone", "iPad", "MacBook"];
        
        const featuredProducts = [];
        
        // Get one product from each featured category
        featuredCategories.forEach(category => {
          // For iPad and MacBook, we need to check variants for price
          const categoryProduct = data.find(product => {
            if (product.category === category) {
              // If it's iPad or MacBook, make sure it has a price or variants
              if (category === "iPad" || category === "MacBook") {
                return product.variants && product.variants.length > 0;
              }
              return true;
            }
            return false;
          });
          
          if (categoryProduct) {
            featuredProducts.push(categoryProduct);
          }
        });
        
        // If we need more products, add from Accessories
        if (featuredProducts.length < 6) {
          const accessoriesProducts = data
            .filter(product => 
              product.category === "Accessories" && 
              (product.price || (product.variants && product.variants.length > 0))
            )
            .slice(0, 6 - featuredProducts.length);
          featuredProducts.push(...accessoriesProducts);
        }
        
        setProducts(featuredProducts.slice(0, 6));
      } catch (err) {
        console.error("Failed to fetch featured products", err);
      }
    };

    fetchProducts();
  }, []);

  // Helper function to get product price
  const getProductPrice = (product) => {
    // If product has direct price
    if (product.price) {
      return `₹ ${product.price.toLocaleString()}`;
    }
    
    // If product has variants with price
    if (product.variants && product.variants.length > 0) {
      // Get minimum price from variants
      const prices = product.variants.map(v => v.price).filter(p => p);
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        
        if (minPrice === maxPrice) {
          return `₹ ${minPrice.toLocaleString()}`;
        } else {
          return `₹ ${minPrice.toLocaleString()} -  ${maxPrice.toLocaleString()}`;
        }
      }
    }
    
    return "Price on request";
  };

  return (
    <section className="featured-section">
      <Container>
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-4"
        >
          FEATURED PRODUCTS
        </motion.h2>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Row className="mt-5">
            {products.map((product, index) => {
              const firstImage =
                product.images?.[0] || "/placeholder.jpg";

              return (
                <Col lg={2} md={4} sm={6} xs={6} key={product._id?.$oid || product._id || index}>
                  <NavLink
                    to={`/product-details/${product._id?.$oid || product._id}`}
                    className="product-link"
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      initial={{ opacity: 0, y: 40 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="product-card"
                    >
                      <img
                        src={firstImage}
                        alt={product.name}
                        onError={(e) =>
                          (e.target.src = "/placeholder.jpg")
                        }
                      />
                      <h6>{product.name}</h6>
                      <div className="price-container">
                        <span className="price">
                          {getProductPrice(product)}
                        </span>
                      </div>
                    </motion.div>
                  </NavLink>
                </Col>
              );
            })}
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default Featured;